/**
 * FAQ Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { faqService } from '../services/faqService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  CreateFaqPayload,
  UpdateFaqPayload,
  GetFaqsParams,
} from '../types/faq.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get all FAQs hook
 */
export function useGetFaqs(
  params?: GetFaqsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.faq, params?.search || ''],
    queryFn: () => faqService.getFaqs(params),
    ...options,
  });
}

/**
 * Get single FAQ hook
 */
export function useGetFaq(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.faq, id],
    queryFn: () => faqService.getFaq(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create FAQ mutation hook
 */
export function useCreateFaq(
  options?: UseMutationOptions<any, any, CreateFaqPayload, any>
) {
  return useApiMutation<any, any, CreateFaqPayload, any>({
    mutationFn: ((data: CreateFaqPayload) => faqService.createFaq(data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.faq] });
    },
    ...options,
  });
}

/**
 * Update FAQ mutation hook
 */
export function useUpdateFaq(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateFaqPayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateFaqPayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateFaqPayload }) =>
      faqService.updateFaq(variables.id, variables.data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.faq] });
    },
    ...options,
  });
}

/**
 * Delete FAQ mutation hook
 */
export function useDeleteFaq(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => faqService.deleteFaq(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.faq] });
    },
    ...options,
  });
}

